import React from "react";
import { Link } from "react-router-dom";
import { useTheme } from "styled-components";

import { HAS_USER_LOGGED_OUT } from "@config/consts";
import { ADMIN_DASHBOARD_LINK } from "@config/links";

import { PowerTurnOn } from "@ui/Assets/MyIcons";
import { TestWrapper } from "@ui/Atoms";

import { useAuth, useAuthManager } from "@hooks/useAuth";
import { useTranslate } from "@hooks/useTranslate";

import { useNavBar } from "../hooks/useNavBar";
import { useAccountNav } from "../hooks/useAccountNav";
import * as VerticalNav from "../VerticalNav";

interface Props {
  onClick: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => void;
}

export const AccountNav: React.FC<Props> = props => {
  const theme = useTheme();
  const translate = useTranslate("batmaid_pwa_navigation");
  const { user, isUserLoggedIn, isUserAdmin } = useAuth();
  const { logout } = useAuthManager();
  const { language, isSSG } = useNavBar();
  const links = useAccountNav(language);

  const logoutHandler = async () => {
    if (!window?.sessionStorage) {
      sessionStorage.setItem(HAS_USER_LOGGED_OUT, "true");
    }

    await logout("system");
    window.location.href = `/${language}/`;
  };

  if (!isUserLoggedIn) {
    return null;
  }

  return (
    <VerticalNav.Nav>
      {isUserAdmin && (
        <TestWrapper testId="adminDashboardLink">
          <VerticalNav.Link
            as="a"
            href={ADMIN_DASHBOARD_LINK}
            $style={{
              default: "medium_18",
              smallTablet: "medium_16",
              smallDesktop: "medium_18",
            }}
          >
            <span>{translate("admin")}</span>
          </VerticalNav.Link>
        </TestWrapper>
      )}

      {isSSG &&
        links.map(link => (
          <TestWrapper key={link.testId} testId={link.testId}>
            <VerticalNav.Link
              as="a"
              href={link.hrefSSG}
              onClick={props.onClick}
              $style={{
                default: "medium_18",
                smallTablet: "medium_16",
                smallDesktop: "medium_18",
              }}
            >
              {!!link.icon && <link.icon width={theme.icons.sizes.base_x4} />}
              <span>{link.label}</span>
            </VerticalNav.Link>
          </TestWrapper>
        ))}

      {!isSSG &&
        links.map(link => (
          <TestWrapper key={link.testId} testId={link.testId}>
            <VerticalNav.Link
              as={Link}
              to={link.href}
              onClick={props.onClick}
              $style={{
                default: "medium_18",
                smallTablet: "medium_16",
                smallDesktop: "medium_18",
              }}
            >
              {!!link.icon && <link.icon width={theme.icons.sizes.base_x4} />}
              <span>{link.label}</span>
            </VerticalNav.Link>
          </TestWrapper>
        ))}

      <TestWrapper testId="logoutButton">
        <VerticalNav.Link
          as="button"
          onClick={logoutHandler}
          $style={{
            default: "medium_18",
            smallTablet: "medium_16",
            smallDesktop: "medium_18",
          }}
        >
          <PowerTurnOn width={theme.icons.sizes.base_x4} />
          <span>{translate("subnav_logout")}</span>
        </VerticalNav.Link>
      </TestWrapper>

      {isUserAdmin && !!user?.get("exitUserUrl") && (
        <TestWrapper testId="exitUserButton">
          <VerticalNav.Link
            as="a"
            href={user.get("exitUserUrl")}
            $style={{
              default: "medium_18",
              smallTablet: "medium_16",
              smallDesktop: "medium_18",
            }}
            $color="red500"
          >
            <PowerTurnOn width={theme.icons.sizes.base_x4} />
            <span>{translate("subnav_exit_user")}</span>
          </VerticalNav.Link>
        </TestWrapper>
      )}
    </VerticalNav.Nav>
  );
};
